/**
 * Branch registry client. The selected branch decides the request's nation,
 * which decides which modules, claim routes and guidance the user sees.
 */
import type { Nation } from '@jeanieiq/types';
import { apiFetch } from './api';

// Re-exported so branch-facing web modules have one import site for both.
export type { Nation };

export interface BranchPublic {
  id: string;
  name: string;
  nation: Nation;
  nationConfirmed: boolean;
  isActive: boolean;
  /**
   * The place of work this branch is (2026-09-30).
   *
   * A branch carries the name and the nation; its site carries the address,
   * the geofence, and everything that joins a person to a location — employee
   * assignments, shifts, clock-ins and kiosks. Null should not happen: the
   * migration linked every branch that existed and every create since writes
   * one — so a screen that finds one should say so, not hide the branch.
   */
  siteId: string | null;
  /**
   * For locums (TODO 1615): the number a locum rings about this shop, and
   * what the pharmacy wants them to know before they arrive. The locum app
   * shows both on the details of a shift here. Optional in the type because a
   * list cached before the server sent them has neither.
   */
  locumContactNumber?: string | null;
  locumNotes?: string | null;
}

/**
 * Creating a branch creates the place of work it is, in one step.
 *
 * The address fields apply only to a NEW branch. The server ignores them on an
 * existing one, so this cannot become a second address editor beside the Sites
 * screen — two doors onto one address being the fault this set out to remove.
 */
export interface BranchCreateInput {
  name: string;
  nation: Nation;
  nationConfirmed?: boolean;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  postcode?: string;
  /** A single-shop pharmacy has no head office, so a branch may be the primary site. */
  isPrimary?: boolean;
  /** Owned or leased, for the site a new branch creates. */
  tenure?: 'owned' | 'leased';
  /** An existing live site this new branch is, instead of creating a second one. */
  siteId?: string;
  /**
   * The GPhC registration this shop trades under. Written to the premises
   * record in the SAME transaction as the branch, so a registration cannot end
   * up filed against a branch name that was never created — which is what the
   * free-text cards on the Premises page allowed.
   */
  gphcPremisesNumber?: string;
  registeredName?: string;
  /**
   * The rest of the GPhC registration, folded in from the premises card
   * (Reagan, 2026-10-01). Only what is SENT is written, so a caller changing
   * a nation cannot blank a superintendent; an empty string clears.
   */
  status?: 'registered' | 'conditions' | 'pending' | 'lapsed';
  superintendent?: string;
  ownerName?: string;
  renewalDate?: string;
  /** For locums. Absent leaves the stored value alone; an empty string clears. */
  locumContactNumber?: string;
  locumNotes?: string;
}

export async function fetchBranches(signal?: AbortSignal): Promise<BranchPublic[]> {
  return apiFetch<BranchPublic[]>('/api/branches', { method: 'GET', signal });
}

export async function upsertBranch(input: BranchCreateInput): Promise<BranchPublic> {
  return apiFetch<BranchPublic>('/api/branches', { method: 'PUT', body: input });
}

export async function setBranchActive(id: string, isActive: boolean): Promise<BranchPublic> {
  return apiFetch<BranchPublic>(`/api/branches/${encodeURIComponent(id)}/active`, {
    method: 'POST', body: { isActive },
  });
}
